import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  CircularProgress,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Edit2, Trash, HambergerMenu, Copy } from "iconsax-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  fetchContactProfileFields,
  createContactProfileField,
  updateContactProfileField,
  deleteContactProfileField,
  saveContactProfileFieldsOrder,
  setContactProfileFieldsOrder,
} from "../../../redux/Slice/Settings/contactProfileFieldsSlice";
import AuthView from "../../../utils/AuthView";

export const CONTACT_PROFILE_FIELD_TYPE_LABELS = {
  text: "Text",
  number: "Number",
  email: "Email",
  phoneNumber: "Phone Number",
};

// Built-in contact properties: shown for reference at the top of the list, same as the
// "(Default)" rows in the column manager this page is modelled on. They can't be edited,
// hidden, removed or reordered here.
const DEFAULT_PROPERTIES = [
  { name: "Name", type: "text" },
  { name: "Business name", type: "text" },
  { name: "Email", type: "email" },
  { name: "Phone Number", type: "phoneNumber" },
];

const EMPTY_FORM = { name: "", type: "text", showInInbox: true };
const GRID_COLUMNS = "36px 90px minmax(160px, 1.4fr) minmax(120px, 1fr) 90px 84px";

const Row = React.forwardRef(function Row({ children, sx, ...rest }, ref) {
  return (
    <Box
      ref={ref}
      sx={{
        display: "grid",
        gridTemplateColumns: GRID_COLUMNS,
        alignItems: "center",
        columnGap: 1,
        px: 2,
        minHeight: 56,
        borderBottom: "1px solid",
        borderColor: "divider",
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
});

const ShownSwitch = (props) => <Switch size="small" color="success" {...props} />;

const SortableFieldRow = ({ field, sequence, canEdit, canDelete, onEdit, onDelete, onToggleShown }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: field._id });

  return (
    <Row
      ref={setNodeRef}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        position: "relative",
        zIndex: isDragging ? 1 : "auto",
        opacity: isDragging ? 0.6 : 1,
        backgroundColor: "background.default",
        "&:hover": { backgroundColor: "action.hover" },
      }}
    >
      <Box
        ref={setActivatorNodeRef}
        {...(canEdit ? { ...attributes, ...listeners } : {})}
        sx={{
          display: "flex",
          color: "text.secondary",
          cursor: canEdit ? "grab" : "default",
          touchAction: "none",
          visibility: canEdit ? "visible" : "hidden",
          "&:active": { cursor: "grabbing" },
        }}
      >
        <HambergerMenu size="18" variant="Linear" />
      </Box>
      <Typography variant="body2">{sequence}</Typography>
      <Typography variant="body2" noWrap title={field.name}>{field.name}</Typography>
      <Typography variant="body2" color="text.secondary">
        {CONTACT_PROFILE_FIELD_TYPE_LABELS[field.type] || field.type}
      </Typography>
      <ShownSwitch checked={field.showInInbox} disabled={!canEdit} onChange={(e) => onToggleShown(field, e.target.checked)} />
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        {canEdit && (
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onEdit(field)}>
              <Edit2 size="16" color="currentColor" variant="Linear" />
            </IconButton>
          </Tooltip>
        )}
        {canDelete && (
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => onDelete(field)}>
              <Trash size="16" color="currentColor" variant="Linear" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Row>
  );
};

const ContactProfileSettings = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const workspaceId = useSelector(
    (s) => s.auth?.user?.workspaceId || s.workspace?.currentWorkspace?._id
  );
  const { items: fields, loading } = useSelector((s) => s.contactProfileFields);
  const roleName = useSelector((s) => s.userDetails?.userDetails?.roleName);
  const perms = useSelector(
    (s) => s.userDetails?.userPermissions?.permissions?.ContactProfileFields || {}
  );
  // Owner bypasses the stored permission checkboxes server-side (roleMiddleware.js), so the
  // UI has to treat Owner as allowed too — same handling as LifecycleSettings.jsx.
  const isOwner = roleName === "Owner";
  const canView = isOwner || perms.View;
  const canAdd = isOwner || perms.Add;
  const canEdit = isOwner || perms.Edit;
  const canDelete = isOwner || perms.Delete;

  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, object = update
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (workspaceId) dispatch(fetchContactProfileFields(workspaceId));
  }, [dispatch, workspaceId]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError("");
    setDialogOpen(true);
  };

  const openEdit = (field) => {
    setEditing(field);
    setForm({ name: field.name, type: field.type, showInInbox: field.showInInbox });
    setError("");
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setError("");
  };

  const handleSave = async () => {
    const name = form.name.trim();
    if (!name) {
      setError("Name is required.");
      return;
    }
    if (!workspaceId) {
      setError("No workspace found. Please select a workspace first.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (editing) {
        await dispatch(updateContactProfileField({
          workspaceId,
          fieldId: editing._id,
          changes: { name, showInInbox: form.showInInbox },
        })).unwrap();
      } else {
        await dispatch(createContactProfileField({
          workspaceId,
          name,
          type: form.type,
          showInInbox: form.showInInbox,
        })).unwrap();
      }
      closeDialog();
    } catch (err) {
      setError(typeof err === "string" ? err : "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleShown = (field, showInInbox) => {
    dispatch(updateContactProfileField({ workspaceId, fieldId: field._id, changes: { showInInbox } }));
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await dispatch(deleteContactProfileField({ workspaceId, fieldId: deleteTarget._id })).unwrap();
      setDeleteTarget(null);
    } catch (err) {
      setError(typeof err === "string" ? err : "Failed to delete.");
      setDeleteTarget(null);
    }
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id || !workspaceId) return;
    const oldIndex = fields.findIndex((f) => f._id === active.id);
    const newIndex = fields.findIndex((f) => f._id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const orderedIds = arrayMove(fields, oldIndex, newIndex).map((f) => f._id);
    dispatch(setContactProfileFieldsOrder(orderedIds));
    const result = await dispatch(saveContactProfileFieldsOrder({ workspaceId, orderedIds }));
    // Server rejected the new order — restore what's actually persisted.
    if (saveContactProfileFieldsOrder.rejected.match(result)) dispatch(fetchContactProfileFields(workspaceId));
  };

  if (!canView) return <AuthView />;

  return (
    <Box sx={{ color: "text.primary" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
          Add your own profile fields for contacts. Fields marked as shown appear in the Profile
          section of the Inbox, where agents can fill them in. Drag the handle to reorder.
        </Typography>
        {canAdd && (
          <Button variant="outlined" onClick={openCreate} sx={{ fontWeight: 600, textTransform: "none" }}>
            Add column
          </Button>
        )}
      </Box>

      {error && !dialogOpen && (
        <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>{error}</Typography>
      )}

      <Box sx={{ overflowX: "auto" }}>
        <Box sx={{ minWidth: 640, border: "1px solid", borderColor: "divider", borderRadius: "8px", overflow: "hidden" }}>
          <Row
            sx={{
              minHeight: 48,
              backgroundColor: theme.palette.mode === "dark" ? "action.hover" : "action.selected",
              "& .MuiTypography-root": { fontWeight: 600 },
            }}
          >
            <span />
            <Typography variant="body2">Sequence</Typography>
            <Typography variant="body2">Property name</Typography>
            <Typography variant="body2">Property type</Typography>
            <Typography variant="body2">Shown</Typography>
            <span />
          </Row>

          {DEFAULT_PROPERTIES.map((property, index) => (
            <Row key={property.name} sx={{ backgroundColor: "background.default" }}>
              <span />
              <Typography variant="body2">{index + 1}</Typography>
              <Typography variant="body2">{property.name} (Default)</Typography>
              <Typography variant="body2" color="text.secondary">
                {CONTACT_PROFILE_FIELD_TYPE_LABELS[property.type]}
              </Typography>
              <ShownSwitch checked disabled />
              <span />
            </Row>
          ))}

          {loading && fields.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={fields.map((f) => f._id)} strategy={verticalListSortingStrategy}>
                {fields.map((field, index) => (
                  <SortableFieldRow
                    key={field._id}
                    field={field}
                    sequence={DEFAULT_PROPERTIES.length + index + 1}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    onEdit={openEdit}
                    onDelete={setDeleteTarget}
                    onToggleShown={handleToggleShown}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </Box>
      </Box>

      {!loading && fields.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No custom fields yet.{canAdd ? " Click Add column to create one." : ""}
        </Typography>
      )}

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { backgroundColor: "background.paper", color: "text.primary" } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>{editing ? "Edit column" : "Add column"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
          {error && <Typography variant="body2" color="error">{error}</Typography>}
          <TextField
            label="Name"
            placeholder="e.g. City"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            fullWidth
            size="small"
            autoFocus
            inputProps={{ maxLength: 50 }}
          />
          {editing?.key && (
            <TextField
              label="Identity key"
              value={editing.key}
              fullWidth
              size="small"
              helperText="Use this key in the signed widget identity (v3 fields) to fill this field automatically."
              InputProps={{
                readOnly: true,
                sx: { fontFamily: "monospace" },
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Copy key">
                      <IconButton
                        size="small"
                        edge="end"
                        onClick={() => navigator.clipboard?.writeText(editing.key).catch(() => {})}
                      >
                        <Copy size="16" color="currentColor" variant="Linear" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          )}
          <FormControl fullWidth size="small" disabled={Boolean(editing)}>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={form.type}
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
            >
              {Object.entries(CONTACT_PROFILE_FIELD_TYPE_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
            {editing && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                The type can't be changed after the column is created.
              </Typography>
            )}
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={form.showInInbox}
                onChange={(e) => setForm((f) => ({ ...f, showInInbox: e.target.checked }))}
              />
            }
            label="Show in Inbox"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : editing ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        PaperProps={{ sx: { backgroundColor: "background.paper", color: "text.primary" } }}
      >
        <DialogTitle>Delete column</DialogTitle>
        <DialogContent>
          <Typography>
            Delete "{deleteTarget?.name}"? The values saved for it on every contact will be removed too.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContactProfileSettings;
